import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

const BrandingContext = createContext();
const CACHE_KEY = "pinpoint_branding_cache";

export const DEFAULT_BRANDING = {
  platform_name: "Pinpoint",
  primary_color: "#F59E0B",
  logo_url:      null,
};

// ── Persistence helpers ────────────────────────────────────────────────────
function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch (_) { return null; }
}
function writeCache(b) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(b)); } catch (_) {}
}

// ── Color helpers ──────────────────────────────────────────────────────────
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function darken(hex, factor = 0.82) {
  const { r, g, b } = hexToRgb(hex);
  const d = v => Math.min(255, Math.round(v * factor)).toString(16).padStart(2, "0");
  return `#${d(r)}${d(g)}${d(b)}`;
}

function applyBrandingCSS(hex) {
  const dark         = darken(hex);
  const { r, g, b } = hexToRgb(hex);
  const light        = `rgba(${r},${g},${b},0.10)`;
  const lightMed     = `rgba(${r},${g},${b},0.18)`;

  const css = `
/* ── Pinpoint primary colour injection ──────────────────────────────── */
.bg-yellow-500, .bg-yellow-400,
.bg-\\[\\#F59E0B\\]                      { background-color: ${hex}  !important; }
.bg-yellow-600,
.bg-\\[\\#D97706\\]                      { background-color: ${dark} !important; }
.hover\\:bg-yellow-600:hover,
.hover\\:bg-\\[\\#D97706\\]:hover        { background-color: ${dark} !important; }
.bg-yellow-50                            { background-color: ${light}    !important; }
.bg-yellow-100                           { background-color: ${lightMed} !important; }

.text-yellow-500, .text-yellow-600,
.text-\\[\\#F59E0B\\]                    { color: ${hex}  !important; }
.text-yellow-700, .text-yellow-800       { color: ${dark} !important; }

.border-yellow-500,
.border-\\[\\#F59E0B\\]                  { border-color: ${hex}  !important; }
.border-yellow-300                       { border-color: rgba(${r},${g},${b},0.40) !important; }

.ring-yellow-500,
.focus\\:ring-\\[\\#F59E0B\\]:focus,
.focus\\:ring-2:focus                    { --tw-ring-color: ${hex} !important; }

.focus\\:border-\\[\\#F59E0B\\]:focus,
.focus\\:border-yellow-500:focus         { border-color: ${hex} !important; }
/* ─────────────────────────────────────────────────────────────────── */
`;

  let el = document.getElementById("__branding_primary__");
  if (!el) {
    el = document.createElement("style");
    el.id = "__branding_primary__";
    document.head.appendChild(el);
  }
  el.textContent = css;
}

// ── Apply all branding side-effects at once ────────────────────────────────
function applyAllBranding(b) {
  const color = b.primary_color || DEFAULT_BRANDING.primary_color;
  document.documentElement.style.setProperty("--color-primary", color);
  document.title = b.platform_name || DEFAULT_BRANDING.platform_name;
  applyBrandingCSS(color);

  const faviconUrl = b.logo_url || "/logo.svg";
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

// ── Apply cached branding BEFORE first render (eliminates flash) ───────────
const _cached = readCache();
if (_cached) applyAllBranding(_cached);

// ── Provider ───────────────────────────────────────────────────────────────
export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(_cached || DEFAULT_BRANDING);
  const [loaded,   setLoaded]   = useState(!!_cached);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "platform", "branding"),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const b = {
            platform_name: data.platform_name || DEFAULT_BRANDING.platform_name,
            primary_color: data.primary_color || DEFAULT_BRANDING.primary_color,
            logo_url:      data.logo_url      || null,
          };
          setBranding(b);
          writeCache(b);
          applyAllBranding(b);
        }
        setLoaded(true);
      },
      (err) => {
        console.error("[BRANDING] Load error:", err.message);
        setLoaded(true);
      }
    );
    return () => unsubscribe();
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loaded }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
